import { signInWithRedirect } from 'aws-amplify/auth';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { PageLayout, Button } from '@/components';
import { usePublicOrganization } from '@/hooks';
import { peekReturnToPracticeSlug } from '@/utils';
import { ROUTES } from '../routes.ts';
import styles from './Login.module.css';
import logoIcon from '../assets/logo-icon-color.svg';
import logoIconWhite from '../assets/logo-icon-white.svg';

export function Login() {
  const location  = useLocation();
  const navigate = useNavigate();
  const authError = (location.state as { authError?: string } | null)?.authError ?? null;
  // Visitors following a practice's link (/praktijk/:slug) are bounced here first; brand the page for that practice.
  const [practiceSlug] = useState(peekReturnToPracticeSlug);
  const { data: practice } = usePublicOrganization(practiceSlug);

  // Cognito's "Already found an entry for username ..." message is technical;
  // map it to a friendlier Dutch message.
  const friendlyError = authError
    ? authError.toLowerCase().includes('already found an entry')
      ? 'Er bestaat al een account met dit e-mailadres via een andere aanmeldmethode. Gebruik de methode waarmee u zich oorspronkelijk heeft geregistreerd.'
      : authError
    : null;

  async function handleStartTeleconsult() {
    try {
      await signInWithRedirect();
    } catch (err) {
      if (err instanceof Error && err.name === 'UserAlreadyAuthenticatedException') {
        navigate('/', { replace: true });
        return;
      }
      throw err;
    }
  }

  const teleconsultCta = (
    <>
      {friendlyError && (
        <p className={styles.authError} role="alert">
          {friendlyError}
        </p>
      )}
      <Button
        variant="primary"
        size="lg"
        className={styles.teleconsultBtn}
        onClick={() => { void handleStartTeleconsult(); }}
      >
        <span className={styles.teleconsultLeft}>
          <img src={logoIconWhite} alt="" aria-hidden="true" className={styles.teleconsultIcon} />
          <span className={styles.teleconsultText}>
            <span className={styles.teleconsultTitle}>Aanmelden</span>
            <span className={styles.teleconsultSub}>Via e-mail / RijksregisterNummer</span>
          </span>
        </span>
      </Button>
    </>
  );

  const pricingInfoCard = (
    <section className={styles.pricingWrap} aria-label="Prijsinformatie teleconsultatie">
      <p className={styles.pricingTab}>KOSTPRIJS</p>

      <div className={styles.pricingCard}>
        <div className={styles.pricingRow}>
          <p className={styles.pricingLabel}>Nieuw teleconsult</p>
          <p className={styles.pricingValue}>€32</p>
        </div>
        <div className={styles.pricingRow}>
          <p className={styles.pricingLabel}>Enkel voorschrift</p>
          <p className={styles.pricingValue}>€5</p>
        </div>
        <div className={styles.pricingRow}>
          <p className={styles.pricingLabel}>Opvolgconsult</p>
          <p className={styles.pricingValue}>€0</p>
        </div>

        <p className={styles.pricingBody}>
          Je dermatoloog beoordeelt, net als bij een gewone consultatie, welk tarief van toepassing is.
        </p>
        <p className={styles.pricingFootnote}>
          <span className={styles.pricingInfoIcon} aria-hidden="true">i</span>
          Nog niet terugbetaald door het ziekenfonds.
        </p>
        <NavLink to={`${ROUTES.platformInfo}#prijs-opgebouwd`} className={styles.pricingLink}>
          Meer details
        </NavLink>
      </div>
    </section>
  );

  return (
    <PageLayout practice={practice}>
      <div className={styles.shell}>
        <div className={styles.brandRow}>
          <img src={logoIcon} alt="Dermalink" className={styles.logoIcon} />
          <p className={styles.brandText}>Dermalink</p>
          <p className={styles.brandSubpill}>{practice?.name ?? 'De Huidspecialisten'}</p>
        </div>

        <div className={styles.shellInner}>
          <div className={styles.shellLeft}>
            <p className={styles.badge}>● TELECONSULTATIE DERMATOLOGIE</p>

            <h1 className={styles.hero}>
              Jouw dermato vraag
              <br />
              <span>op afstand beantwoord.</span>
            </h1>

            <p className={styles.lead}>
              Geen wachtzaal, geen onbekende arts. Jouw dermatoloog bekijkt je foto&apos;s en geeft binnen 5 werkdagen antwoord.
            </p>

            {/* Mobile-only button — hidden on desktop */}
            <div className={styles.btnWrapMobile}>{teleconsultCta}</div>

            <p className={styles.sectionLabel}>WAT MAG JE VERWACHTEN</p>

            <ul className={styles.benefitList}>
              <li className={styles.benefitItem}>
                <span className={styles.checkCircle}>✓</span>
                <div>
                  <p className={styles.benefitTitle}>Je eigen dermatoloog</p>
                  <p className={styles.benefitDesc}>
                    Geen onbekende arts - je vraag gaat naar wie jouw dossier kent.
                  </p>
                </div>
              </li>
              <li className={styles.benefitItem}>
                <span className={styles.checkCircle}>✓</span>
                <div>
                  <p className={styles.benefitTitle}>Foto&apos;s, vraag, antwoord</p>
                  <p className={styles.benefitDesc}>
                    Upload foto&apos;s, stel je vraag. Antwoord binnen 5 werkdagen.
                  </p>
                </div>
              </li>
              <li className={styles.benefitItem}>
                <span className={styles.checkCircle}>✓</span>
                <div>
                  <p className={styles.benefitTitle}>Voorschrift indien nodig</p>
                  <p className={styles.benefitDesc}>Gekoppeld aan je identiteitskaart.</p>
                </div>
              </li>
            </ul>
          </div>

          <div className={styles.shellRight}>
            <div className={styles.warningWrap}>
              <p className={styles.warningBadge}>OPGELET</p>
              <div className={styles.warningBox}>
                <span className={styles.warningIcon} aria-hidden="true">!</span>
                <div className={styles.warningContent}>
                  <p className={styles.warningTitle}>Niet voor controle van vlekken</p>
                  <p className={styles.warningText}>
                    Voor één specifieke vlek: boek een <strong>1-vlek check</strong> afspraak. Voor
                    algemene controle: maak een afspraak in de praktijk.
                  </p>
                </div>
              </div>
            </div>

            <div className={styles.btnWrap}>{teleconsultCta}</div>
            <div className={styles.pricingDesktop}>{pricingInfoCard}</div>
          </div>
        </div>

        <div className={styles.pricingMobile}>{pricingInfoCard}</div>

        <p className={styles.trustMeta}>
          Veilig <span className={styles.metaDot}>·</span> Persoonlijk<span className={styles.metaDot}>·</span> Eenvoudig
        </p>
      </div>
    </PageLayout>
  );
}
